import { useMemo, useCallback } from 'react'
import { getImage, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import useRecords from '../../../../views/indexing/hooks/useRecords'
import useViewIndexPersonSelect from '../../../../views/indexing/components/selectPersonV2/useViewIndexPersonSelect'

/**
 * @typedef {Object} HouseholdWithPersons
 * @property {Object} household - The household element
 * @property {Array} persons - Filtered persons within this household
 */

/**
 * @typedef {Object} HouseholdListProps
 * @property {Array<HouseholdWithPersons>} filteredHouseholdsWithPersons - Array of households with their filtered persons
 * @property {Array} filteredFloatingPersons - Array of persons not associated with any household
 * @property {boolean} showLoading - Loading state indicator
 * @property {boolean} isJiapu - Whether the current image is a Jiapu type
 */

/**
 * @typedef {Object} UseHouseholdListReturn
 * @property {HouseholdListProps} householdListProps - Props to be passed to HouseholdList component
 * @property {boolean} showBlankState - Boolean indicating if the blank state should be shown
 * @property {boolean} showChangeHistory - Boolean indicating if change history should be shown
 * @property {boolean} loading - Loading state indicator
 */

/**
 * Hook that manages the logic for the HouseholdList component, including filtering and selection state.
 *
 * @param {Object} params - The hook parameters
 * @param {boolean} [params.loading=false] - Boolean indicating external loading state
 * @param {string} [params.filterValue=''] - String to filter persons by name
 * @returns {UseHouseholdListReturn} The hook return values
 */
const useHouseholdList = ({ loading = false, filterValue = '' }) => {
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const onPersonSelect = useViewIndexPersonSelect()

  const { currentImageArk, images, personOrder, loading: slsLoading } = zionSlsState
  const currentImage = getImage(images, currentImageArk)
  const personIdsOrderForImage = personOrder?.find((po) => po.partitionKey === currentImageArk)

  const { allRecords, floatingPersons, noPeople, imageHasData, isJiapu } = useRecords({
    images,
    currentImageArk,
    personIdsOrder: personIdsOrderForImage?.personIds,
    thumbnails,
  })

  const showLoading = loading || (noPeople && slsLoading)

  // Filter persons by name
  const filterPersonsByName = useCallback(
    (persons) => {
      if (!filterValue.trim()) return persons
      const lowerFilter = filterValue.toLowerCase()

      return persons.filter((person) => {
        const name = getPersonName({ person })?.toLowerCase() || ''
        return name.includes(lowerFilter)
      })
    },
    [filterValue]
  )

  // Get all persons from records and filter them
  const filteredHouseholdsWithPersons = useMemo(() => {
    if (!allRecords) return []
    return allRecords.map((record) => {
      const personsInRecord =
        record?.subElements?.filter((sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === currentImageArk) ||
        []
      return { household: record, persons: filterPersonsByName(personsInRecord) }
    })
  }, [allRecords, currentImageArk, filterPersonsByName])

  // Filter floating persons
  const filteredFloatingPersons = useMemo(
    () => filterPersonsByName(floatingPersons || []),
    [floatingPersons, filterPersonsByName]
  )

  const hasRecords = currentImage?.subElements?.length > 0

  // Computed state flags
  const showBlankState = !loading && !slsLoading && (noPeople || !imageHasData || isJiapu)
  const showChangeHistory = !loading && !slsLoading && imageHasData && hasRecords && !isJiapu

  return {
    householdListProps: {
      filteredHouseholdsWithPersons,
      filteredFloatingPersons,
      showLoading,
      isJiapu,
      onPersonSelect,
    },
    showBlankState,
    showChangeHistory,
    loading: showLoading,
  }
}

export default useHouseholdList
